import React from 'react';
import { inject, observer } from 'mobx-react';
import styles from './index.module.scss';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import { BASE_IMG_URL } from '@images/imageUrls';
import { Image, Spin } from 'antd';
import Toast from '@components/Toast';
import { uploadAttachment, postsCreate } from '../../../server/api';
import EmojiPicker from '@components/EmojiPicker/h5';

interface CommentInputProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    type?: 'post' | 'reply';  // 'post' 主评论 'reply' 回复评论
    threadId?: any;
    postId?: any;  // 如果是回复，需要提供被回复的评论 id（主楼ID）
    commentPostId?: any; // 回复二级评论时，目标评论ID
    replyToUser?: any;  // 回复对象用户信息 { nickname, userId }
    commentType?: number;  // 发布评论时的type参数 0=帖子评论 2=活动评论，默认0
    onSubmit?: (data: { content: string; images: any[]; newComment?: any }) => Promise<any>;
    onCancel?: () => void;
}

interface CommentInputState {
    content: string;
    images: any[];
    emojiPickerVisible: boolean;
    uploading: boolean;
}

@inject('app', 'site', 'user')
@observer
class H5CommentInput extends React.Component<CommentInputProps, CommentInputState> {
    private fileInputRef = React.createRef<HTMLInputElement>();
    private textareaRef = React.createRef<HTMLTextAreaElement>();

    state: CommentInputState = {
        content: '',
        images: [],
        emojiPickerVisible: false,
        uploading: false,
    };

    componentDidMount() {
        // 表情从 EmojiPopup 独立管理
    }

    // 处理文本内容变化
    handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ content: e.target.value });
    };

    // 处理表情选择
    handleEmojiSelect = (emojiCode: string) => {
        const { content } = this.state;
        const textarea = this.textareaRef.current;
        if (textarea) {
            const { selectionStart = 0 } = textarea;
            const newContent = content.slice(0, selectionStart) + emojiCode + content.slice(selectionStart);
            this.setState({ content: newContent }, () => {
                setTimeout(() => {
                    if (textarea) {
                        textarea.focus();
                        textarea.setSelectionRange(selectionStart + emojiCode.length, selectionStart + emojiCode.length);
                    }
                }, 0);
            });
        }
    };

    // 打开表情选择器
    handleEmojiClick = () => {
        const { emojiPickerVisible } = this.state;

        this.setState({ emojiPickerVisible: !emojiPickerVisible });
    };

    // 关闭表情选择器
    handleEmojiClose = () => {
        this.setState({ emojiPickerVisible: false });
    };

    handleEmojiDelete = () => {
        const { content } = this.state;
        const textarea = this.textareaRef.current;

        if (!textarea) return;

        const { selectionStart = 0, selectionEnd = 0 } = textarea;
        if (selectionStart !== selectionEnd) {
            const nextContent = content.slice(0, selectionStart) + content.slice(selectionEnd);
            this.setState({ content: nextContent }, () => {
                setTimeout(() => {
                    textarea.focus();
                    textarea.setSelectionRange(selectionStart, selectionStart);
                }, 0);
            });
            return;
        }

        if (selectionStart <= 0) return;

        const contentBeforeCursor = content.slice(0, selectionStart);
        const previousChar = (Array.from(contentBeforeCursor).pop() as string | undefined) || '';
        const deleteLength = previousChar.length || 1;
        const nextCursor = Math.max(selectionStart - deleteLength, 0);
        const nextContent = content.slice(0, nextCursor) + content.slice(selectionStart);

        this.setState({ content: nextContent }, () => {
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(nextCursor, nextCursor);
            }, 0);
        });
    };

    // 处理上传图片
    handlePhotoClick = () => {
        const { images } = this.state;
        if (images.length >= 1) {
            Toast.info('最多只能上传1张图片');
            return;
        }
        this.fileInputRef.current?.click();
    };

    // 处理文件选择
    handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        const { images } = this.state;
        if (images.length + files.length > 1) {
            Toast.info('最多只能上传1张图片');
            return;
        }

        this.setState({ uploading: true });
        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const formData = new FormData();
                formData.append('type', '1');  // 1-图片
                formData.append('file', file);
                
                // 调用现有的上传附件接口
                const res = await uploadAttachment(formData);
                if (res && res.code === 0 && res.data) {
                    this.setState(prev => ({
                        images: [...prev.images, {
                            url: res.data!.url,
                            id: res.data!.id || Math.random().toString(36).substr(2, 9)
                        }]
                    }));
                } else {
                    Toast.info('图片上传失败');
                }
            }
        } catch (error) {
            console.error('上传错误:', error);
            Toast.info('图片上传出错');
        } finally {
            this.setState({ uploading: false });
            if (this.fileInputRef.current) {
                this.fileInputRef.current.value = '';
            }
        }
    };

    // 删除图片
    handleRemoveImage = (id: string) => {
        this.setState(prev => ({
            images: prev.images.filter(img => img.id !== id)
        }));
    };

    // 提交评论
    handleSubmit = async () => {
        const { content, images } = this.state;
        const { onSubmit, threadId, postId, commentPostId, type, commentType = 0 } = this.props;

        if (!content.trim()) {
            Toast.info('请输入评论内容');
            return;
        }

        this.setState({ uploading: true });

        // 构造接口参数
        const params: any = {
            id: threadId,          // 线程ID
            type: commentType,     // 评论类型：0=帖子评论 2=活动评论
            content: content,
            source: '',
        };

        if (type === 'reply' && postId) {
            params.replyPostId = postId;                // 楼中楼：被回复的主楼ID
            if (commentPostId) params.commentPostId = commentPostId; // 楼中楼回复他人：目标评论ID
        }

        // 图片附件，携带顺序
        if (images.length > 0) {
            params.attachments = images.map((img, index) => ({ id: img.id, order: index + 1 }));
        }

        // 调用接口
        const res = await postsCreate(params);

        if (res && (res.Code === 0 || res.code === 0) && (res.Data || res.data)) {
            const data = res.Data || res.data;
            Toast.success('发布成功');

            // 清空输入框
            this.setState({ content: '', images: [], emojiPickerVisible: false });

            // 如果有自定义的 onSubmit 回调，传递格式化后的新评论数据
            if (onSubmit) {
                const { replyToUser } = this.props;
                const newComment = {
                    id: data.id,
                    type: data.type,
                    content: data.content,
                    plainText: data.plainText,
                    source: data.source || '',
                    ipRegion: data.ipRegion || '',
                    commentDecoration: data.commentDecoration,
                    createdAt: data.createdAt,
                    updatedAt: data.updatedAt,
                    likeCount: data.likeCount || 0,
                    replyCount: data.replyCount || 0,
                    isLiked: data.isLiked || false,
                    isSink: data.isSink || false,
                    isSticky: data.isSticky || false,
                    user: data.user,
                    images: data.images || [],
                    postable: data.postable,
                    permissions: data.permissions,
                    replyPostId: data.replyPostId,
                    replyUserId: data.replyUserId,
                    commentPostId: data.commentPostId,
                    commentUserId: data.commentUserId,
                    replyUser: data.replyUser,
                    commentUser: data.commentUser,
                };

                // 如果 commentPostId 有值，说明是回复二级评论，赋值给 commentUser
                // 否则赋值给 replyUser
                if (commentPostId && replyToUser) {
                    if (!newComment.commentUser) {
                        newComment.commentUser = replyToUser;
                    }
                    if (!newComment.commentUserId && replyToUser.id) {
                        newComment.commentUserId = replyToUser.id;
                    }
                } else if (!newComment.replyUser && replyToUser) {
                    newComment.replyUser = replyToUser;
                    if (!newComment.replyUserId && replyToUser.id) {
                        newComment.replyUserId = replyToUser.id;
                    }
                }

                await onSubmit({ content, images, newComment });
            }
        } else {
            const msg = res?.Message || res?.msg || '发布失败';
            Toast.info(msg);
        }

        this.setState({ uploading: false });
    };

    // 取消
    handleCancel = () => {
        this.setState({ content: '', images: [], emojiPickerVisible: false });
        this.props.onCancel?.();
    };

    render() {
        const { content, images, emojiPickerVisible, uploading } = this.state;
        const { replyToUser } = this.props;

        return (
            <>
                {/* 遮罩层 */}
                <div className={styles.mask} onClick={this.handleCancel} />
                
                <div className={styles.h5CommentInputContainer}>
                {/* {replyToUser && (
                    <div className={styles.replyToInfo}>
                        回复 <span className={styles.replyToName}>{replyToUser.nickname}</span>
                    </div>
                )} */}

                <Spin spinning={uploading}>
                    {/* 灰色输入框 */}
                    <div className={styles.inputBox}>
                        <textarea
                            ref={this.textareaRef}
                            className={styles.textarea}
                            placeholder='分享脑洞'
                            value={content}
                            onChange={this.handleContentChange}
                        />

                        {/* 图片预览区域 */}
                        {images.length > 0 && (
                            <div className={styles.imagePreview}>
                                {images.map((img) => (
                                    <div key={img.id} className={styles.imageItem}>
                                        <Image
                                            width="100%"
                                            height="100%"
                                            src={img.url}
                                            preview={{ mask: '预览' }}
                                        />
                                        <div
                                            className={styles.removeIcon}
                                            onClick={() => this.handleRemoveImage(img.id)}
                                        >
                                            ×
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* 底部工具栏 */}
                    <div className={styles.toolbar}>
                        <div className={styles.toolIcons}>
                            <img
                                src={`${BASE_IMG_URL}/common/setPost/photoBar.png`}
                                alt="upload"
                                className={styles.toolIcon}
                                onClick={this.handlePhotoClick}
                            />
                            <img
                                src={`${BASE_IMG_URL}/common/setPost/expression.png`}
                                alt="emoji"
                                className={styles.toolIcon}
                                onClick={this.handleEmojiClick}
                            />
                        </div>

                        <button
                            className={`${styles.submitBtn} ${content.trim() ? styles.active : ''}`}
                            onClick={this.handleSubmit}
                            disabled={!content.trim()}
                        >
                            发送
                        </button>
                    </div>

                    <EmojiPicker
                        visible={emojiPickerVisible}
                        onClose={this.handleEmojiClose}
                        onSelect={(emojiValue: string, emojiCode?: string) => this.handleEmojiSelect(emojiCode || emojiValue)}
                        onDelete={this.handleEmojiDelete}
                        inFlow
                        showEmojiTab
                        showCategoryTabs
                    />
                </Spin>

                {/* 隐藏的文件输入 */}
                <input
                    ref={this.fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={this.handleFileChange}
                    style={{ display: 'none' }}
                />
            </div>
            </>
        );
    }
}

export default H5CommentInput;
